import {
  declaredSourceNumber,
  isForwardingProven,
} from '../../shared/lib/businessNumber.js';
import { useEffect, useRef, useState } from 'react';
import { formatPhoneDisplay } from '../../shared/lib/phoneFormat.js';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { Badge, BrandLoader, Button, Card, Icon } from '@acs/ui';
import { useActivateBusiness } from '../businesses/index.js';
import { ForwardingUnavailable } from './NumberUnavailable.js';
import { BusinessLineField } from './BusinessLineField.js';
import { ForwardingPanel, useForwardingWindow } from './ForwardingPanel.js';
import { useSyncComplianceStatus } from './hooks.js';
import { useI18n } from '../../i18n/index.js';

/**
 * Everything about the business's phone number — activation, the forwarding
 * instructions, the divert check and the numbers it holds. Rendered as the
 * Number section of Settings rather than a top-level nav entry: it's setup you
 * come back to occasionally, not somewhere you work day to day.
 */
export function NumberSettings() {
  const business = useActiveBusiness();
  useReconcileOnLoad(business.id, business.status);
  return <StatusFlow business={business} />;
}

/**
 * Self-heal a missed Twilio approval callback: while the business is under review,
 * reconcile the local status against Twilio's live bundle status once per page
 * load. The business doc is live-subscribed, so an approval flips the UI on its
 * own. Fires once per business (keyed on id), not on a poll interval.
 */
function useReconcileOnLoad(businessId: string, status: BusinessDoc['status']) {
  const sync = useSyncComplianceStatus();
  const syncedFor = useRef<string | null>(null);
  useEffect(() => {
    if (status !== 'pending_review') return;
    if (syncedFor.current === businessId) return;
    syncedFor.current = businessId;
    sync.mutate({ businessId });
    // `sync` is a stable mutation object; re-run only when the target business changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, status]);
}

function StatusFlow({ business }: { business: BusinessDoc }) {
  const { t } = useI18n();
  // Doc 18 §7.5: the owner's own line is the subject of this page. Once they
  // have declared it — with or without a forwarding target assigned yet — the
  // number section drives everything, because the target is plumbing that
  // appears only while the divert is being set up.
  if (business.forwardNumber) return <ForwardNumberSection business={business} />;

  switch (business.status) {
    case 'provisioning':
      return (
        <Card title={t('number.provisioning')}>
          <p className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
            <BrandLoader size={24} />
            <span className="muted">{t('number.provisioningHint')}</span>
          </p>
        </Card>
      );
    case 'suspended':
      return (
        <Card title={t('number.suspended')} actions={<Badge tone="danger">{t('number.suspendedBadge')}</Badge>}>
          <p className="muted">{t('number.suspendedHint')}</p>
        </Card>
      );
    case 'archived':
      return (
        <Card title={t('number.archived')} actions={<Badge tone="neutral">{t('number.archivedBadge')}</Badge>}>
          <p className="muted">{t('number.archivedHint')}</p>
        </Card>
      );
    // draft, collecting_docs, rejected, pending_review, approved — and `active`
    // if the number ever went missing. None of these has a number, and since we
    // stopped running KYC, activation is the only way to get one. The states
    // used to route to the bundle-backed provisioning form and numbers list,
    // which can't complete without a Twilio bundle: a business sitting on
    // `approved` was shown a form that could never succeed and no way forward.
    default:
      return <ActivateSection business={business} />;
  }
}

/**
 * Draft business (doc 13a): the forward-number activation is now the only route
 * in. The regulated local-number path used to live under a disclosure here; we
 * don't run KYC ourselves for the time being, so there's nothing to choose
 * between — one number, one button.
 */
function ActivateSection({ business }: { business: BusinessDoc }) {
  const { t } = useI18n();
  const activate = useActivateBusiness();
  const declared = declaredSourceNumber(business);

  // Assign the forwarding target automatically once we know their line — see
  // the wizard for why this is not a button the owner presses. `declared` has
  // to come from `declaredSourceNumber`: until provisioning runs, the owner's
  // line lives in `pendingSourceNumber`, so reading `forwardNumber.sourceNumber`
  // made this condition unsatisfiable and nothing was ever provisioned.
  const needsTarget = declared != null && business.forwardNumber == null;
  useEffect(() => {
    if (needsTarget && activate.isIdle) activate.mutate(business.id);
  }, [needsTarget, activate, business.id]);

  // One failure state, the same as the wizard's: nothing is bought, so a failed
  // connection to the shared line is either "not live in this country" or "on
  // our side — try again" (see `ForwardingUnavailable`).
  if (activate.isError) {
    return (
      <div className="grid">
        <Card title={t('number.forwardingSetupFailed')}>
          <ForwardingUnavailable
            businessName={business.name}
            country={business.country}
            error={activate.error}
            onRetry={() => activate.mutate(business.id)}
            retrying={activate.isPending}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="grid">
      <Card title={t('number.title')}>
        <p className="muted">{t('number.keepItHint')}</p>
        <BusinessLineField business={business} />
        {needsTarget && (
          <p className="row" style={{ gap: 10, flexWrap: 'nowrap', marginTop: 12 }}>
            <BrandLoader size={24} />
            <span className="muted">{t('number.preparing')}</span>
          </p>
        )}
      </Card>
    </div>
  );
}

/**
 * The forward-number home (doc 13a / doc 18): the owner's own line, then the
 * divert onto it.
 *
 * Two cards, not four. This section used to print the forwarding target three
 * times, warn twice that it isn't the owner's number, and carry its own copy of
 * the verification flow that the setup wizard also had — worded differently, so
 * the same owner was told two different things in two places. All of that now
 * comes from {@link ForwardingPanel}, which the wizard renders too.
 *
 * Changing the line is edited HERE, in the card that prints it, and the field
 * takes the big number's place while it is open. It is the one thing on this
 * page that is about the number itself rather than about the divert, and an
 * owner looking for it looks at the number they can see.
 *
 * "Change" is offered whatever state the line is in. It used to appear only
 * once the line was PROVEN, on the reasoning that an unproven line is already
 * editable as step 1 of the panel below — true, but it left the number printed
 * large at the top of the page with no way to touch it, which is exactly the
 * screen an owner who has just mistyped that number is looking at. A typo is
 * most likely BEFORE the check passes, not after.
 *
 * This is the page's ONLY editor for that number. The panel below used to open
 * with a "Confirm your business number" step carrying a second copy of the same
 * field, directly under the card that prints it — so the page asked twice and
 * could save the one the owner wasn't looking at. The panel's verification
 * window is shared rather than copied, so this field freezes mid-check for the
 * same reason that step's did: the arriving call is compared against whatever
 * is stored, and the server refuses the write regardless.
 */
function ForwardNumberSection({ business }: { business: BusinessDoc }) {
  const { t } = useI18n();
  const declared = formatPhoneDisplay(declaredSourceNumber(business));
  const verified = isForwardingProven(business);
  const [editing, setEditing] = useState(false);
  const checkWindow = useForwardingWindow(business);

  return (
    <div className="grid">
      <Card
        title={t('number.title')}
        actions={
          <>
            {verified ? (
              <Badge tone="success">{t('number.forwardingLive')}</Badge>
            ) : (
              <Badge tone="warning">{t('number.notAnsweringYet')}</Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={editing ? undefined : <Icon name="edit" size={15} />}
              onClick={() => setEditing((open) => !open)}
            >
              {t(editing ? 'common.cancel' : 'number.editNumber')}
            </Button>
          </>
        }
      >
        {/* Their number, never ours. Ours under a business heading reads as
            "this is your new number" and ends up printed on things (doc 18 §7.4). */}
        {editing ? (
          <>
            <BusinessLineField
              business={business}
              locked={checkWindow.waiting ? 'check' : null}
              onDeclared={() => setEditing(false)}
            />
            {verified && <p className="muted">{t('number.editHint')}</p>}
          </>
        ) : (
          <>
            <p className="number-hero">
              <span className="mono">{declared ?? '—'}</span>
            </p>
            <p className="muted">
              {t(verified ? 'number.liveHint' : 'number.notLiveHint')}
            </p>
          </>
        )}
      </Card>

      <Card title={t('number.forwarding')}>
        <ForwardingPanel business={business} checkWindow={checkWindow} />
      </Card>
    </div>
  );
}
